import UpgradeType from 'app/store/UpgradeType.js';
import { isAutoApplyCoupon } from '@app/utils/coupon.utils';
import { uniqueBy } from '@app/utils/collection.utils';

function Order(orderData) {
  var data = orderData || {};

  this.subjectFirstName = data.subjectFirstName || null;
  this.subjectLastName = data.subjectLastName || null;
  this.subjectGrade = data.subjectGrade || null;
  this.subjectRelationship = data.subjectRelationship || null;
  this.subjectTeacherName = data.subjectTeacherName || null;
  this.subjectDetailsDeferred = data.subjectDetailsDeferred || null;
  this.dontKnowTeacherName = data.dontKnowTeacherName || null;
  this.orderTotalAmount = data.orderTotalAmount || 0.0;
  this.totalAmount = data.totalAmount || 0.0;
  this.taxAmount = data.taxAmount || 0.0;
  this.shippingAddress = data.shippingAddress || null;
  this.orderPromotion = data.orderPromotion || [];
  this.packages = data.packages || [];
  this.addOns = data.addOns || [];
  this.bundles = data.bundles || [];
  this.upgrades = data.upgrades || [];
  this.fees = data.fees || [];
  this.nameOnText = data.nameOnText || null;
  this.optOutOfPersonalizationEnhancements = data.optOutOfPersonalizationEnhancements || false;
  this.optOutOfRetouchingEnhancements = data.optOutOfRetouchingEnhancements || false;
  this.availableShippingMethods = data.availableShippingMethods || [];
  this.shipping = data.shipping || null;
  this.portraitDashboard = data.portraitDashboard || null;
  this.createdDateTime = data.createdDateTime || null;
  this.id = data.id || null;
  this.allowPersonalizationNamePrefill = data.allowPersonalizationNamePrefill || false;
  this.shippingAddressRequired = data.shippingAddressRequired || false;
  this.studentId = data.studentId;
  this.masterSubjectId = data.masterSubjectId;
  this.favouriteLooks = data.favouriteLooks || [];
  this.unlockedPremiumBgs = new Set();
  this.premiumBgAddons = [];
}

Order.prototype.addPackage = function (pkg) {
  this.packages.push(pkg);
};

Order.prototype.addAddOn = function (addOn) {
  this.addOns.push(addOn);
};

Order.prototype.addUpgrade = function (upgrade) {
  this.upgrades.push(upgrade);
};

Order.prototype.addBundle = function (bundle) {
  this.bundles.push(bundle);
};

Order.prototype.updatePackage = function (pkg, index) {
  this.packages[index] = pkg;
};

Order.prototype.updateAddOn = function (addOn, index) {
  this.addOns[index] = addOn;
};

Order.prototype.updateUpgrade = function (upgrade, index) {
  this.upgrades[index] = upgrade;
};

Order.prototype.updateBundle = function (bundle, index) {
  this.bundles[index] = bundle;
};

Order.prototype.deletePackage = function (index) {
  return this.packages.splice(index, 1);
};

Order.prototype.deleteAddOn = function (index) {
  return this.addOns.splice(index, 1);
};

Order.prototype.deleteBundle = function (index) {
  return this.bundles.splice(index, 1);
};

Order.prototype.deleteUpgrade = function (index) {
  return this.upgrades.splice(index, 1);
};

Order.prototype.getDisplayName = function () {
  return this.subjectFirstName;
};

Order.prototype.getSubTotal = function () {
  // MLT-1140 2016.09.28 - Fix javascript addition issue.  100% coupon may ask for billing info because of
  //                       a fractional amount remaining
  return (
    (this.getOrderLines()
      .map((line) => line.getUnitPrice())
      .reduce((sum, price) => sum + price, 0) *
      10) /
    10
  );
};

Order.prototype.getSlashThroughPrices = function () {
  return (R.sum(R.map(R.invoker(0, 'getSlashThroughPriceDiscount'), this.getOrderLines())) * 10) / 10;
};

//calculate Line discounts from package, bundles, addon and upgrades added to cart.
Order.prototype.getPromotionAmount = function () {
  const sumAmount = R.compose(R.sum, R.map(R.prop('amount')));
  //Accumulate promotions which do not have coupon code for instant savings
  const nonCouponOrderPromotions = this.orderPromotion.filter(
    (promo) => promo.couponCode === null || isAutoApplyCoupon(promo)
  );
  const sumLineDiscounts = R.compose(
    R.sum,
    R.map(function (line) {
      if (line.orderPromotion?.length > 0 && line.orderPromotion[0].couponCode !== null) {
        return 0;
      }
      return line.orderable.unitPrice - line.salesPrice;
    })
  );

  const promotionAmount = sumLineDiscounts(this.getOrderLines()) + sumAmount(nonCouponOrderPromotions);
  return promotionAmount || 0;
};

//Flatten all the package, bundles, addon and upgrades added to cart.
Order.prototype.getOrderLines = function () {
  return R.flatten([this.packages, this.addOns, this.upgrades, this.bundles, this.premiumBgAddons]);
};

Order.prototype.getOrderLineDetails = function () {
  return this.getOrderLines().flatMap((line) => line.orderLineDetails);
};

Order.prototype.hasOrderLines = function () {
  return this.getOrderLines().length > 0;
};

//Total Amount of one subject. calculated from shipping amount , tax amount ,
//orderLine promotion amount and order promotion.
Order.prototype.getTotal = function () {
  return this.getSubTotal() + this.getShippingUnitPrice() - this.getPromotionAmount();
};

Order.prototype.getShippingPrice = function () {
  return this.shipping?.salesPrice || 0.0;
};

Order.prototype.getShippingUnitPrice = function () {
  const promotions = this.shipping?.orderable?.promotions ?? [];
  const shippingPromotionAmount = promotions
    .filter((p) => p.couponCode === null || isAutoApplyCoupon(p))
    .reduce((sum, p) => sum + p.amount, 0);

  const unitPrice = this.shipping?.orderable?.unitPrice ?? 0;
  return unitPrice - (shippingPromotionAmount || 0);
};

Order.prototype.getNumberOfItemsInCart = function () {
  return this.packages.length + this.addOns.length + this.bundles.length + this.premiumBgAddons.length;
};

Order.prototype.getUpgradesByType = function () {
  var personalizationEnhancement, retouchingEnhancement;
  R.forEach(function (orderLine) {
    if (orderLine.orderable.type === UpgradeType.PERSONALIZATION) {
      personalizationEnhancement = orderLine;
    } else if (
      orderLine.orderable.type === UpgradeType.PREMIUM_RETOUCHING ||
      orderLine.orderable.type === UpgradeType.BASIC_RETOUCHING
    ) {
      retouchingEnhancement = orderLine;
    }
  }, this.upgrades);

  return { personalizationEnhancement: personalizationEnhancement, retouchingEnhancement: retouchingEnhancement };
};

Order.prototype.fetchDigitalDeliveries = function () {
  var getDigitalDeliveries = R.pipe(
      R.map(function (orderLine) {
        return !!orderLine.orderLineDetails
          ? R.pluck('digitalDelivery')(R.pluck('item')(orderLine.orderLineDetails))
          : [];
      }),
      R.flatten
    ),
    digitalDeliveries = getDigitalDeliveries(this.getOrderLines());

  return digitalDeliveries;
};

Order.prototype.hasDigitalItem = function () {
  return R.any(R.eq(true))(this.fetchDigitalDeliveries());
};

Order.prototype.hasNonDigitalItem = function () {
  return R.any(R.eq(false))(this.fetchDigitalDeliveries());
};

Order.prototype.hasLongTailItem = function () {
  var getLongTailDeliveries = R.pipe(
      R.map(function (orderLine) {
        return !!orderLine.orderLineDetails
          ? R.pluck('productCategory')(R.pluck('item')(orderLine.orderLineDetails))
          : [];
      }),
      R.flatten
    ),
    longTailDeliveries = getLongTailDeliveries(this.getOrderLines());

  return R.any(R.eq('MagAndLam') || R.eq('Gifts'))(longTailDeliveries);
};

Order.prototype.resetPremiumBackgroundToDefault = function (backgroundImageId, defaultLook) {
  this._resetBackgroundInOrderLines(this.packages, backgroundImageId, defaultLook);
  this._resetBackgroundInOrderLines(this.addOns, backgroundImageId, defaultLook);
};

Order.prototype._resetBackgroundInOrderLines = function (orderLines, backgroundImageId, defaultLook) {
  orderLines.forEach((orderLine) => {
    orderLine.orderLineDetails?.forEach((detail) => {
      if (detail.look?.background?.backgroundImageId === backgroundImageId) {
        detail.look = defaultLook;
      }
    });
  });
};

Order.prototype.getPremiumBackgrounds = function () {
  return uniqueBy(
    this.getOrderLineDetails()
      .map((orderLineDetail) => orderLineDetail?.look?.background)
      .filter((background) => background?.premiumUpgrade),
    (bg) => bg.backgroundImageId
  );
};

export default Order;
